"use client";

import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ExternalLink,
  Sparkles,
  Globe,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  SlidersHorizontal,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchClientBrands } from "@/store/slices/landingSlice";
import { imageUrl } from "@/services/BaseUrl";
import type { ClientBrand } from "@/types/landing";

const ClientProjectsGrid: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: clientBrands, loading } = useAppSelector(
    (state) => state.landing.clientBrands,
  );
  const { mode } = useAppSelector((state) => state.theme);
  const isDark = mode === "dark";

  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState<"carousel" | "grid">("carousel");
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  // Cards visible per view based on responsive layout
  const [cardsPerPage, setCardsPerPage] = useState(4);

  useEffect(() => {
    dispatch(fetchClientBrands());
  }, [dispatch]);

  // Adjust cards per page based on window width
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setCardsPerPage(1);
      } else if (window.innerWidth < 1024) {
        setCardsPerPage(2);
      } else if (window.innerWidth < 1280) {
        setCardsPerPage(3);
      } else {
        setCardsPerPage(4);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-play timer for carousel
  useEffect(() => {
    if (
      viewMode === "carousel" &&
      isAutoPlaying &&
      clientBrands.length > cardsPerPage
    ) {
      autoPlayRef.current = setInterval(() => {
        handleNext();
      }, 4000);
    }
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [
    currentIndex,
    isAutoPlaying,
    viewMode,
    clientBrands.length,
    cardsPerPage,
  ]);

  const maxIndex = Math.max(0, clientBrands.length - cardsPerPage);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const getFullImageUrl = (path?: string): string => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    const cleanPath = path.startsWith("/") ? path.substring(1) : path;
    return `${imageUrl}/${cleanPath}`;
  };

  const getThemeLogo = (item: ClientBrand): string => {
    if (isDark && item.logoDark) {
      return getFullImageUrl(item.logoDark);
    }
    return getFullImageUrl(item.logo);
  };

  return (
    <section
      className={`py-16 sm:py-24 relative overflow-hidden transition-colors duration-700 ${isDark ? "bg-[#0B0F19]" : "bg-slate-50"}`}
    >
      {/* Background Glow & Grid Boxes Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full blur-[140px] transition-opacity duration-700 ${
            isDark ? "bg-[#8B31FF]/15 opacity-70" : "bg-[#8B31FF]/8 opacity-40"
          }`}
        />
        <div
          className={`absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full blur-[100px] transition-opacity duration-700 ${
            isDark ? "bg-[#ec4899]/10 opacity-50" : "bg-[#ec4899]/5 opacity-30"
          }`}
        />
        {/* Grid pattern of small boxes */}
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${isDark ? "opacity-[0.03]" : "opacity-[0.06]"}`}
          style={{
            backgroundImage: isDark
              ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
              : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 sm:mb-14"
        >
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className={`inline-flex items-center gap-2 mb-3 px-4 py-1.5 rounded-full border backdrop-blur-md transition-colors duration-300 ${
                isDark
                  ? "border-[#8B31FF]/30 bg-[#8B31FF]/10 text-[#8B31FF]"
                  : "border-purple-200 bg-purple-50 text-[#8B31FF]"
              }`}
            >
              <Sparkles className="w-4 h-4" />
              <span className="font-bold text-xs uppercase tracking-wider">
                Client Websites & Brands
              </span>
            </motion.div>

            <h2
              className={`text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Our Client{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">
                Portfolio
              </span>
            </h2>
            <p
              className={`text-xs sm:text-sm md:text-base mt-2 max-w-2xl transition-colors duration-300 ${isDark ? "text-gray-400" : "text-gray-600"}`}
            >
              Discover live projects and websites created for our partners and
              clients worldwide. Click any card to visit the live site.
            </p>
          </div>

          {/* Controls Bar */}
          <div className="flex items-center gap-3 self-start md:self-auto">
            {/* View Mode Toggle */}
            <div
              className={`flex items-center p-1 rounded-lg border ${isDark ? "bg-gray-800/80 border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
            >
              <button
                onClick={() => setViewMode("carousel")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  viewMode === "carousel"
                    ? "bg-gradient-to-r from-[#8B31FF] to-[#6d1ec9] text-white shadow-sm"
                    : isDark
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                }`}
                title="Carousel View"
              >
                <SlidersHorizontal size={14} />
                <span className="hidden sm:inline">Carousel</span>
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  viewMode === "grid"
                    ? "bg-gradient-to-r from-[#8B31FF] to-[#6d1ec9] text-white shadow-sm"
                    : isDark
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                }`}
                title="Grid View"
              >
                <LayoutGrid size={14} />
                <span className="hidden sm:inline">Grid</span>
              </button>
            </div>

            {/* Carousel Arrow Buttons */}
            {viewMode === "carousel" && clientBrands.length > cardsPerPage && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrev}
                  onMouseEnter={() => setIsAutoPlaying(false)}
                  onMouseLeave={() => setIsAutoPlaying(true)}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center border transition-all cursor-pointer ${
                    isDark
                      ? "bg-gray-800/80 border-gray-700 text-gray-300 hover:bg-[#8B31FF] hover:border-[#8B31FF] hover:text-white shadow-md"
                      : "bg-white border-gray-200 text-gray-700 hover:bg-[#8B31FF] hover:border-[#8B31FF] hover:text-white shadow-sm"
                  }`}
                  aria-label="Previous Slide"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={handleNext}
                  onMouseEnter={() => setIsAutoPlaying(false)}
                  onMouseLeave={() => setIsAutoPlaying(true)}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center border transition-all cursor-pointer ${
                    isDark
                      ? "bg-gray-800/80 border-gray-700 text-gray-300 hover:bg-[#8B31FF] hover:border-[#8B31FF] hover:text-white shadow-md"
                      : "bg-white border-gray-200 text-gray-700 hover:bg-[#8B31FF] hover:border-[#8B31FF] hover:text-white shadow-sm"
                  }`}
                  aria-label="Next Slide"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Loading State Skeleton */}
        {loading && clientBrands.length === 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((idx) => (
              <div
                key={idx}
                className={`h-52 rounded-lg border animate-pulse p-6 flex flex-col items-center justify-center ${
                  isDark
                    ? "bg-gray-800/40 border-gray-700/50"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="w-24 h-16 bg-gray-300 dark:bg-gray-700 rounded-lg mb-4" />
                <div className="w-32 h-4 bg-gray-300 dark:bg-gray-700 rounded" />
              </div>
            ))}
          </div>
        )}

        {/* Cards Container */}
        {!loading && clientBrands.length > 0 && (
          <div>
            {viewMode === "carousel" ? (
              /* CAROUSEL VIEW */
              <div
                className="overflow-hidden py-2"
                onMouseEnter={() => setIsAutoPlaying(false)}
                onMouseLeave={() => setIsAutoPlaying(true)}
              >
                <motion.div
                  className="flex gap-6 transition-transform duration-500 ease-out"
                  animate={{
                    x: `-${currentIndex * (100 / cardsPerPage)}%`,
                  }}
                  transition={{ type: "spring", stiffness: 260, damping: 28 }}
                >
                  {clientBrands.map((item, index) => {
                    const logoUrlToUse = getThemeLogo(item);
                    const targetUrl =
                      item.websiteUrl && item.websiteUrl !== "#"
                        ? item.websiteUrl
                        : null;

                    return (
                      <div
                        key={item._id || item.id || index}
                        className="flex-shrink-0"
                        style={{
                          width: `calc(${100 / cardsPerPage}% - ${(24 * (cardsPerPage - 1)) / cardsPerPage}px)`,
                        }}
                      >
                        <a
                          href={targetUrl || "#"}
                          target={targetUrl ? "_blank" : undefined}
                          rel={targetUrl ? "noopener noreferrer" : undefined}
                          onClick={(e) => {
                            if (!targetUrl) e.preventDefault();
                          }}
                          className={`group relative rounded-lg border p-6 flex flex-col items-center justify-between text-center transition-all duration-300 cursor-pointer overflow-hidden h-56 ${
                            isDark
                              ? "bg-gray-900/70 border-gray-800 hover:border-[#8B31FF]/60 hover:bg-gray-800/80 shadow-lg hover:shadow-2xl hover:shadow-[#8B31FF]/20"
                              : "bg-white border-gray-200/90 hover:border-purple-300 hover:bg-white shadow-sm hover:shadow-xl hover:shadow-purple-500/10"
                          } hover:-translate-y-2`}
                        >
                          {/* Accent bar using dynamic item.gradient (shows on hover) */}
                          <div
                            className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${item.gradient || "from-[#8B31FF] to-cyan-500"} opacity-0 group-hover:opacity-100 transition-all duration-300`}
                          />

                          {/* Link Icon */}
                          {targetUrl && (
                            <div
                              className={`absolute top-3.5 right-3.5 p-1.5 rounded-full transition-all duration-300 ${
                                isDark
                                  ? "bg-gray-800 text-gray-400 group-hover:text-white group-hover:bg-[#8B31FF]"
                                  : "bg-gray-100 text-gray-400 group-hover:text-white group-hover:bg-[#8B31FF]"
                              }`}
                              title="Visit Website"
                            >
                              <ExternalLink size={14} />
                            </div>
                          )}

                          {/* Logo */}
                          <div className="w-full h-28 flex items-center justify-center p-3 my-1">
                            {logoUrlToUse ? (
                              <img
                                src={logoUrlToUse}
                                alt={item.name}
                                className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                                onError={(e) => {
                                  if (
                                    item.logo &&
                                    (e.target as HTMLImageElement).src !==
                                      getFullImageUrl(item.logo)
                                  ) {
                                    (e.target as HTMLImageElement).src =
                                      getFullImageUrl(item.logo);
                                  }
                                }}
                              />
                            ) : (
                              <div className="flex items-center gap-2 text-gray-400">
                                <Globe size={28} />
                              </div>
                            )}
                          </div>

                          {/* Name & Industry */}
                          <div className="w-full pt-3 border-t border-gray-100 dark:border-gray-800">
                            <h3
                              className={`font-bold text-base tracking-tight transition-colors duration-300 group-hover:text-[#8B31FF] ${
                                isDark ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {item.name}
                            </h3>
                            {item.industry && (
                              <p
                                className={`text-xs mt-0.5 transition-colors duration-300 ${
                                  isDark ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                {item.industry}
                              </p>
                            )}
                          </div>
                        </a>
                      </div>
                    );
                  })}
                </motion.div>

                {/* Pagination Dots */}
                {clientBrands.length > cardsPerPage && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    {Array.from({ length: maxIndex + 1 }).map((_, dotIdx) => (
                      <button
                        key={dotIdx}
                        onClick={() => setCurrentIndex(dotIdx)}
                        className={`transition-all duration-300 rounded-full cursor-pointer ${
                          currentIndex === dotIdx
                            ? "w-8 h-2.5 bg-gradient-to-r from-[#8B31FF] to-[#6d1ec9]"
                            : isDark
                              ? "w-2.5 h-2.5 bg-gray-700 hover:bg-gray-600"
                              : "w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400"
                        }`}
                        aria-label={`Go to slide ${dotIdx + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* GRID VIEW */
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {clientBrands.map((item, index) => {
                  const logoUrlToUse = getThemeLogo(item);
                  const targetUrl =
                    item.websiteUrl && item.websiteUrl !== "#"
                      ? item.websiteUrl
                      : null;

                  return (
                    <motion.a
                      key={item._id || item.id || index}
                      href={targetUrl || "#"}
                      target={targetUrl ? "_blank" : undefined}
                      rel={targetUrl ? "noopener noreferrer" : undefined}
                      onClick={(e) => {
                        if (!targetUrl) e.preventDefault();
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.04 }}
                      className={`group relative rounded-lg border p-6 flex flex-col items-center justify-between text-center transition-all duration-300 cursor-pointer overflow-hidden h-56 ${
                        isDark
                          ? "bg-gray-900/70 border-gray-800 hover:border-[#8B31FF]/60 hover:bg-gray-800/80 shadow-lg hover:shadow-2xl hover:shadow-[#8B31FF]/20"
                          : "bg-white border-gray-200/90 hover:border-purple-300 hover:bg-white shadow-sm hover:shadow-xl hover:shadow-purple-500/10"
                      } hover:-translate-y-2`}
                    >
                      {/* Accent bar using dynamic item.gradient (shows on hover) */}
                      <div
                        className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${item.gradient || "from-[#8B31FF] to-cyan-500"} opacity-0 group-hover:opacity-100 transition-all duration-300`}
                      />

                      {targetUrl && (
                        <div
                          className={`absolute top-3.5 right-3.5 p-1.5 rounded-full transition-all duration-300 ${
                            isDark
                              ? "bg-gray-800 text-gray-400 group-hover:text-white group-hover:bg-[#8B31FF]"
                              : "bg-gray-100 text-gray-400 group-hover:text-white group-hover:bg-[#8B31FF]"
                          }`}
                          title="Visit Website"
                        >
                          <ExternalLink size={14} />
                        </div>
                      )}

                      <div className="w-full h-28 flex items-center justify-center p-3 my-1">
                        {logoUrlToUse ? (
                          <img
                            src={logoUrlToUse}
                            alt={item.name}
                            className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                            onError={(e) => {
                              if (
                                item.logo &&
                                (e.target as HTMLImageElement).src !==
                                  getFullImageUrl(item.logo)
                              ) {
                                (e.target as HTMLImageElement).src =
                                  getFullImageUrl(item.logo);
                              }
                            }}
                          />
                        ) : (
                          <div className="flex items-center gap-2 text-gray-400">
                            <Globe size={28} />
                          </div>
                        )}
                      </div>

                      <div className="w-full pt-3 border-t border-gray-100 dark:border-gray-800">
                        <h3
                          className={`font-bold text-base tracking-tight transition-colors duration-300 group-hover:text-[#8B31FF] ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {item.name}
                        </h3>
                        {item.industry && (
                          <p
                            className={`text-xs mt-0.5 transition-colors duration-300 ${
                              isDark ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {item.industry}
                          </p>
                        )}
                      </div>
                    </motion.a>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default ClientProjectsGrid;
